import React, { createContext, useContext, useState, useEffect } from 'react';

// Extend Window interface to include our custom property
declare global {
  interface Window {
    navigationDarkApplied?: boolean;
  }
}

const DARK_MODE_STORAGE_KEY = 'darkMode';

interface DarkModeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  setDarkMode: (isDark: boolean) => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

export const useDarkMode = (): DarkModeContextType => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

interface DarkModeProviderProps {
  children: React.ReactNode;
}

export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Check if user has a saved preference
    const saved = localStorage.getItem(DARK_MODE_STORAGE_KEY);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    
    // Check system preference
    if (window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    return false;
  });

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const setDarkMode = (isDark: boolean) => {
    setIsDarkMode(isDark);
  };

  // Function to force colored bars to be visible
  const forceColoredBars = () => {
    if (!isDarkMode) return;
    
    // Force colored bars for summary cards
    const summaryCards = document.querySelectorAll('.conduct-summary-card');
    summaryCards.forEach((card, index) => {
      const lineElement = card.querySelector('[class*="line"]') as HTMLElement;
      if (lineElement) {
        // Set colors based on card position
        if (index === 0) {
          lineElement.style.backgroundColor = '#ff5252'; // Red for High Risk
        } else if (index === 1) {
          lineElement.style.backgroundColor = '#ffab00'; // Orange for Investigate
        } else if (index === 2) {
          lineElement.style.backgroundColor = '#00b6a5'; // Green for Clear
        }
      }
    });
    
    // Force colored bars for toggle cards
    const toggleCards = document.querySelectorAll('.MuiPaper-root[class*="summaryContainer"]');
    toggleCards.forEach((card, index) => {
      const eventLineElement = card.querySelector('[class*="eventLine"]') as HTMLElement;
      const pepLineElement = card.querySelector('[class*="pepLine"]') as HTMLElement;
      
      if (eventLineElement) {
        eventLineElement.style.backgroundColor = '#1083c6'; // Blue for Events
      }
      if (pepLineElement) {
        pepLineElement.style.backgroundColor = '#A743EE'; // Purple for PEP
      }
    });
  };

  // Function to force risk indicator colors to be more prominent
  const forceRiskIndicatorColors = () => {
    if (!isDarkMode) return;
    
    // NUCLEAR OPTION: Force colors on ALL elements with inline background colors
    const allElements = document.querySelectorAll('*');
    allElements.forEach((element) => {
      const el = element as HTMLElement;
      const inlineStyle = el.getAttribute('style');
      
      if (inlineStyle && inlineStyle.includes('background-color')) {
        // Check if it's a risk indicator color
        if (inlineStyle.includes('rgb(255, 82, 82)') || inlineStyle.includes('#ff5252')) {
          el.style.setProperty('background-color', '#ff5252', 'important');
          el.style.setProperty('box-shadow', '0 0 4px rgba(255, 82, 82, 0.5)', 'important');
        } else if (inlineStyle.includes('rgb(255, 171, 0)') || inlineStyle.includes('#ffab00')) {
          el.style.setProperty('background-color', '#ffab00', 'important');
          el.style.setProperty('box-shadow', '0 0 4px rgba(255, 171, 0, 0.5)', 'important');
        } else if (inlineStyle.includes('rgb(0, 182, 165)') || inlineStyle.includes('#00b6a5')) {
          el.style.setProperty('background-color', '#00b6a5', 'important');
          el.style.setProperty('box-shadow', '0 0 4px rgba(0, 182, 165, 0.5)', 'important');
        } else if (inlineStyle.includes('rgb(158, 158, 158)') || inlineStyle.includes('#9e9e9e')) {
          el.style.setProperty('background-color', '#9e9e9e', 'important');
          el.style.setProperty('box-shadow', '0 0 4px rgba(158, 158, 158, 0.5)', 'important');
        }
      }
    });

    // Also target specific classes we know exist
    const conductElements = document.querySelectorAll('[class*="conduct"], [class*="globalIndicator"], .jss15, .css-ii0mqj');
    conductElements.forEach((element) => {
      const el = element as HTMLElement;
      const inlineStyle = el.getAttribute('style');
      
      if (inlineStyle && inlineStyle.includes('background-color')) {
        if (inlineStyle.includes('rgb(255, 82, 82)') || inlineStyle.includes('#ff5252')) {
          el.style.setProperty('background-color', '#ff5252', 'important');
        } else if (inlineStyle.includes('rgb(255, 171, 0)') || inlineStyle.includes('#ffab00')) {
          el.style.setProperty('background-color', '#ffab00', 'important');
        } else if (inlineStyle.includes('rgb(0, 182, 165)') || inlineStyle.includes('#00b6a5')) {
          el.style.setProperty('background-color', '#00b6a5', 'important');
        } else if (inlineStyle.includes('rgb(158, 158, 158)') || inlineStyle.includes('#9e9e9e')) {
          el.style.setProperty('background-color', '#9e9e9e', 'important');
        }
      }
    });
  };

  // Function to force navigation bar to be dark - optimized
  const forceNavigationDark = () => {
    if (!isDarkMode) return;
    
    // Only run once per session to avoid performance issues
    if (window.navigationDarkApplied) return;
    
    // Force navigation bar to be dark - simplified approach
    const navigationElements = document.querySelectorAll('[class*="desktop_content"], [class*="mobile_content"]');
    navigationElements.forEach((element) => {
      const el = element as HTMLElement;
      el.style.backgroundColor = '#1e1e1e';
      el.style.color = '#ffffff';
    });
    
    // Force search bar contents to be dark
    const searchBarContents = document.querySelectorAll('[class*="search_bar_contents"]');
    searchBarContents.forEach((element) => {
      const el = element as HTMLElement;
      el.style.backgroundColor = '#1e1e1e';
      el.style.color = '#ffffff';
    });
    
    // Force search bar to be dark
    const searchBars = document.querySelectorAll('[class*="search_bar"]');
    searchBars.forEach((element) => {
      const el = element as HTMLElement;
      el.style.backgroundColor = '#1e1e1e';
      el.style.borderColor = '#404040';
    });
    
    // Force search inputs to be dark
    const searchInputs = document.querySelectorAll('input[type="search"], input[placeholder*="Search"]');
    searchInputs.forEach((input) => {
      const inputEl = input as HTMLElement;
      inputEl.style.backgroundColor = '#1e1e1e';
      inputEl.style.color = '#ffffff';
      inputEl.style.borderColor = '#404040';
    });
    
    // Force label manager and summary areas to be dark
    const labelAreas = document.querySelectorAll('[class*="labelManager"], [class*="labelSummary"]');
    labelAreas.forEach((element) => {
      const el = element as HTMLElement;
      el.style.backgroundColor = '#1e1e1e';
      el.style.color = '#ffffff';
    });
    
    // Mark as applied to prevent repeated execution
    window.navigationDarkApplied = true;
  };

  // Function to cleanup forced dark styles when switching to light mode
  const cleanupForcedDarkStyles = () => {
    // Remove forced styles from navigation elements
    const navigationElements = document.querySelectorAll('[class*="desktop_content"], [class*="mobile_content"]');
    navigationElements.forEach((element) => {
      const el = element as HTMLElement;
      el.style.removeProperty('background-color');
      el.style.removeProperty('color');
    });
    
    // Remove forced styles from search bar contents
    const searchBarContents = document.querySelectorAll('[class*="search_bar_contents"]');
    searchBarContents.forEach((element) => {
      const el = element as HTMLElement;
      el.style.removeProperty('background-color');
      el.style.removeProperty('color');
    });
    
    // Remove forced styles from search bars
    const searchBars = document.querySelectorAll('[class*="search_bar"]');
    searchBars.forEach((element) => {
      const el = element as HTMLElement;
      el.style.removeProperty('background-color');
      el.style.removeProperty('border-color');
    });
    
    // Remove forced styles from search inputs
    const searchInputs = document.querySelectorAll('input[type="search"], input[placeholder*="Search"]');
    searchInputs.forEach((input) => {
      const inputEl = input as HTMLElement;
      inputEl.style.removeProperty('background-color');
      inputEl.style.removeProperty('color');
      inputEl.style.removeProperty('border-color');
    });
    
    // Remove forced styles from label manager and summary areas
    const labelAreas = document.querySelectorAll('[class*="labelManager"], [class*="labelSummary"]');
    labelAreas.forEach((element) => {
      const el = element as HTMLElement;
      el.style.removeProperty('background-color');
      el.style.removeProperty('color');
    });
    
    // Clean up forced colored bars
    const summaryCards = document.querySelectorAll('.conduct-summary-card');
    summaryCards.forEach((card) => {
      const lineElement = card.querySelector('[class*="line"]') as HTMLElement;
      if (lineElement) {
        lineElement.style.removeProperty('background-color');
      }
    });
    
    const toggleCards = document.querySelectorAll('.MuiPaper-root[class*="summaryContainer"]');
    toggleCards.forEach((card) => {
      const eventLineElement = card.querySelector('[class*="eventLine"]') as HTMLElement;
      const pepLineElement = card.querySelector('[class*="pepLine"]') as HTMLElement;
      
      if (eventLineElement) {
        eventLineElement.style.removeProperty('background-color');
      }
      if (pepLineElement) {
        pepLineElement.style.removeProperty('background-color');
      }
    });
    
    // Clean up forced risk indicator colors
    const conductElements = document.querySelectorAll('[class*="conduct"]');
    conductElements.forEach((element) => {
      const el = element as HTMLElement;
      el.style.removeProperty('background-color');
      el.style.removeProperty('box-shadow');
    });

    // Clean up forced global indicator colors
    const globalIndicatorElements = document.querySelectorAll('[class*="globalIndicator"]');
    globalIndicatorElements.forEach((element) => {
      const el = element as HTMLElement;
      el.style.removeProperty('background-color');
      el.style.removeProperty('border-color');
    });
    
    // Additional cleanup for any other potential forced styles
    const allElementsWithForcedStyles = document.querySelectorAll('*[style*="background-color: #1e1e1e"], *[style*="background-color: #2d2d2d"], *[style*="color: #ffffff"]');
    allElementsWithForcedStyles.forEach((element) => {
      const el = element as HTMLElement;
      const style = el.style;
      
      // Only remove styles that were likely forced by our dark mode functions
      if (style.backgroundColor === '#1e1e1e' || style.backgroundColor === '#2d2d2d') {
        style.removeProperty('background-color');
      }
      if (style.color === '#ffffff') {
        style.removeProperty('color');
      }
      if (style.borderColor === '#404040') {
        style.removeProperty('border-color');
      }
    });
    
    // Reset the flag
    window.navigationDarkApplied = false;
  };

  // Save to localStorage whenever dark mode changes
  useEffect(() => {
    localStorage.setItem(DARK_MODE_STORAGE_KEY, JSON.stringify(isDarkMode));
    
    // Apply dark mode class to body for global CSS
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
      // Force colored bars and navigation dark after a short delay
      setTimeout(() => {
        forceColoredBars();
        forceNavigationDark();
        forceRiskIndicatorColors();
      }, 100);
      
      // Also run it again after a longer delay to catch any dynamically added elements
      setTimeout(() => {
        forceRiskIndicatorColors();
      }, 1000);
      
      // Set up an interval to continuously check for new elements
      const intervalId = setInterval(() => {
        forceRiskIndicatorColors();
      }, 2000);
      
      // Store the interval ID so we can clear it later
      (window as any).riskIndicatorInterval = intervalId;
    } else {
      document.body.classList.remove('dark-mode');
      // Clean up forced styles when switching to light mode
      setTimeout(() => {
        cleanupForcedDarkStyles();
      }, 50);
      
      // Clear the interval when switching to light mode
      if ((window as any).riskIndicatorInterval) {
        clearInterval((window as any).riskIndicatorInterval);
        (window as any).riskIndicatorInterval = null;
      }
    }
  }, [isDarkMode]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a preference
      const saved = localStorage.getItem(DARK_MODE_STORAGE_KEY);
      if (saved === null) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const value: DarkModeContextType = {
    isDarkMode,
    toggleDarkMode,
    setDarkMode,
  };

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};

export default DarkModeProvider;